import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import { occasions } from "@/mocks/products";

export default function OccasionsPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const [active, setActive] = useState(occasions[0].id);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeOcc = occasions.find((o) => o.id === active) || occasions[0];

  return (
    <div className="min-h-screen bg-[#f5ede0]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-amber-900 via-amber-800 to-stone-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20gift%20occasions%20display%20with%20flowers%20candles%20and%20gift%20boxes%20for%20different%20celebrations%20on%20elegant%20cream%20background%2C%20warm%20golden%20light%2C%20premium%20boutique%2C%20romantic%20and%20festive&width=1920&height=400&seq=occ_hero&orientation=landscape"
            alt="Occasions"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center py-10">
          <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-3">— Every Moment Matters —</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("occ_title")}
          </h1>
          <p className="text-white/70 text-lg">{t("occ_subtitle")}</p>
        </div>
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        {/* Occasion Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-14">
          {occasions.map((occ, i) => (
            <button
              key={occ.id}
              onClick={() => setActive(occ.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                active === occ.id
                  ? "bg-gradient-to-r from-amber-700 to-amber-500 text-white scale-105"
                  : "bg-white text-gray-600 border border-amber-100 hover:border-amber-300 hover:text-amber-700"
              }`}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: `all 0.5s ease`,
                transitionDelay: `${i * 0.07}s`,
              }}
            >
              <i className={`${occ.icon} text-base`}></i>
              {isFr ? occ.labelF : occ.label}
            </button>
          ))}
        </div>

        {/* Active Occasion Display */}
        <div
          className="grid lg:grid-cols-2 gap-12 items-center transition-all duration-500"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.3s" }}
        >
          {/* Image */}
          <div className="relative rounded-3xl overflow-hidden h-[500px] group">
            <img
              key={activeOcc.id}
              src={activeOcc.image}
              alt={isFr ? activeOcc.labelF : activeOcc.label}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            {/* Beige overlay instead of rose/pink */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#5c3d1e]/70 via-[#c8a97e]/10 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-700 to-amber-500 text-white text-sm font-semibold">
                <i className={`${activeOcc.icon} text-base`}></i>
                {isFr ? activeOcc.labelF : activeOcc.label}
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">
              — Perfect Gifts For —
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-stone-800 mb-5 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {isFr ? activeOcc.labelF : activeOcc.label}
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              {isFr ? activeOcc.descF : activeOcc.desc}
            </p>

            {/* Gift suggestions */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: "ri-plant-line", label: isFr ? "Fleurs Fraîches" : "Fresh Flowers" },
                { icon: "ri-fire-line", label: isFr ? "Bougies Parfumées" : "Scented Candles" },
                { icon: "ri-gift-2-line", label: isFr ? "Boîtes Cadeaux" : "Gift Boxes" },
                { icon: "ri-cup-line", label: isFr ? "Gelées Maison" : "Homemade Jellies" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-amber-100">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-600 to-amber-400 flex-shrink-0">
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={`/shop?category=giftboxes`}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-amber-700 to-amber-500 text-white font-semibold rounded-full hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line text-base"></i>
                {t("occ_shop_now")}
              </Link>
              <a
                href={`https://wa.me/237678012159?text=Hello! I need gift ideas for ${isFr ? activeOcc.labelF : activeOcc.label}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-green-500 text-white font-semibold rounded-full hover:bg-green-400 hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base"></i>
                {isFr ? "Demander conseil" : "Get Advice"}
              </a>
            </div>
          </div>
        </div>

        {/* All Occasions Grid */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Toutes les Occasions" : "All Occasions"}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {occasions.map((occ, i) => (
              <button
                key={occ.id}
                onClick={() => { setActive(occ.id); window.scrollTo({ top: 300, behavior: "smooth" }); }}
                className={`group relative rounded-2xl overflow-hidden h-48 cursor-pointer transition-all duration-300 hover:-translate-y-1 ${active === occ.id ? "ring-2 ring-amber-500 ring-offset-2" : ""}`}
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(30px)",
                  transition: `all 0.5s ease`,
                  transitionDelay: `${0.5 + i * 0.07}s`,
                }}
              >
                <img src={occ.image} alt={isFr ? occ.labelF : occ.label} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110" />
                {/* Beige/warm overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#5c3d1e]/75 via-[#c8a97e]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-600 to-amber-400 mb-2">
                    <i className={`${occ.icon} text-white text-sm`}></i>
                  </div>
                  <p className="text-white font-bold text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {isFr ? occ.labelF : occ.label}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Side scroll indicator — beige styled */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-40 hidden lg:flex">
        {["home", "tabs", "detail", "grid"].map((_, i) => (
          <div
            key={i}
            className="w-1.5 h-6 rounded-full bg-amber-300/60 hover:bg-amber-500 transition-colors cursor-pointer"
          />
        ))}
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}